import { createInformationApi, updateInformationApi } from "@/api/services/admin/information";
import { useEnum } from "@/api/services/global/enum";
import { Button } from "@/modules/irohana-components/elements/Button";
import { Editor } from "@/modules/irohana-components/elements/Editor";
import { Form } from "@/modules/irohana-components/elements/Form";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { Input } from "@/modules/irohana-components/elements/Input";
import { InputDatePicker } from "@/modules/irohana-components/elements/InputDatePicker";
import { RadioGroup } from "@/modules/irohana-components/elements/RadioGroup";
import { Switch } from "@/modules/irohana-components/elements/Switch";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { FILE_UPLOAD_FOR, MIME_TYPES } from "@/constants/file";
import { informationSchema } from "@/features/information/schema/informationSchema";
import { TInformationForm } from "@/features/information/types/informationForm";
import { convertInformationFormToCreateReq, convertInformationFormToUpdateReq } from "@/features/information/utils/convert";
import { getInformationStatus, INFORMATION_STATUS, isEndDateLocked, isStartDateLocked } from "@/features/information/utils/informationStatus";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { TExtendFile } from "@/types/file";
import { dayjs } from "@/utils/dayjs";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { styled } from "@mui/system";
import { PreviewInformationModal } from "@/features/information/components/PreviewInformationModal";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { UploadFile } from "@/features/information/components/UploadFile";
import { FileItem } from "@/modules/irohana-components/elements/FileItem";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { convertKbToByte } from "@/utils/file";
import { TopPage } from "@/modules/irohana-components/elements/TopPage";

type TInformationFormProps = {
  previousData?: Partial<TInformationForm>;
  backUrl?: string;
};

const initForm: TInformationForm = {
  title: "",
  content: "",
  enableEndDate: false,
  enableStartDate: false,
  endDate: null,
  filePath: null,
  startDate: null,
  file: null,
  type: "",
};

export const InformationForm: FC<TInformationFormProps> = ({ previousData, backUrl }) => {
  const { id } = useParams();
  const { data: informationTypes } = useEnum("EInformationType");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const methods = useForm<TInformationForm>({
    defaultValues: previousData ?? initForm,
    resolver: yupResolver(informationSchema),
    mode: "onBlur",
    context: { savedEndDate: previousData?.endDate ?? null },
  });
  const { control, clearErrors, getValues, setValue } = methods;
  const { enableStartDate, enableEndDate } = useWatch({ control });
  const startDateWatch = useWatch({ control, name: "startDate" });
  const endDateWatch = useWatch({ control, name: "endDate" });
  const [openPreview, setOpenPreview] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileUploaded, setFileUploaded] = useState<File | null>(null);
  const filePath = useWatch({ control, name: "filePath" });
  const [informationStatus, setInformationStatus] = useState<string>(INFORMATION_STATUS.DRAFT);
  // Locks are derived from the saved record, not from live form values, so they don't flip while editing.
  const lockStartDate = useMemo(() => !!id && isStartDateLocked(previousData), [id, previousData]);
  const lockEndDate = useMemo(() => !!id && isEndDateLocked(previousData?.endDate), [id, previousData]);
  const [isSubmit, setIsSubmit] = useState(false);
  const acceptMimeTypes = [
    MIME_TYPES.PDF,
    MIME_TYPES.WORD,
    MIME_TYPES.EXCEL,
    MIME_TYPES.TXT,
    MIME_TYPES.IMAGE,
    MIME_TYPES.CSV,
    MIME_TYPES.STACK,
    MIME_TYPES.PPT,
  ].flat();

  // Skip the first run so loaded form values aren't overwritten on mount (would trigger leave-confirm blocker).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      setInformationStatus(getStatus());
      return;
    }
    // Toggling off only drops the errors: the entered date is kept so it comes back when toggled on again.
    if (!enableStartDate) {
      clearErrors("startDate");
    }
    if (!enableEndDate) {
      clearErrors("endDate");
    }
    const status = getStatus();
    setInformationStatus(status);
  }, [enableStartDate, enableEndDate, clearErrors]);

  // Flipping a date switch on seeds today only when the picker is empty. Comparing against the previous
  // toggle state keeps this to the off -> on transition, so a date cleared by hand while the switch stays
  // on is never refilled, and mount is a no-op.
  const prevEnableDatesRef = useRef({ start: enableStartDate, end: enableEndDate });
  useEffect(() => {
    const prev = prevEnableDatesRef.current;
    prevEnableDatesRef.current = { start: enableStartDate, end: enableEndDate };
    if (enableStartDate && !prev.start && !getValues("startDate")) {
      setValue("startDate", dayjs());
    }
    if (enableEndDate && !prev.end && !getValues("endDate")) {
      setValue("endDate", dayjs());
    }
  }, [enableStartDate, enableEndDate, getValues, setValue]);

  useEffect(() => {
    clearErrors("endDate");
  }, [startDateWatch, clearErrors]);

  useEffect(() => {
    clearErrors("startDate");
  }, [endDateWatch, clearErrors]);

  const onSubmit = async (data: TInformationForm) => {
    await handleSaveData(data, false);
  };

  const handleSaveDraft = async () => {
    try {
      const payload = getValues();
      await handleSaveData(payload, true);
      methods.reset();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const handleSaveData = async (data: TInformationForm, isDraft: boolean) => {
    setIsSubmit(true);
    try {
      if (id) {
        const payload = convertInformationFormToUpdateReq(data, isDraft);
        payload.type = payload.type || null;
        payload.startDate = payload.startDate ? dayjs(payload.startDate).format("YYYY-MM-DD") : null;
        payload.endDate = payload.endDate ? dayjs(payload.endDate).format("YYYY-MM-DD") : null;
        payload.file = fileUploaded as TExtendFile;
        const rs = await updateInformationApi(id, payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INFORMATION.FETCH_DETAIL, id.toString()] });
      } else {
        const payload = convertInformationFormToCreateReq(data, isDraft);
        payload.type = payload.type || null;
        payload.file = fileUploaded as TExtendFile;
        const rs = await createInformationApi(payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INFORMATION.FETCH_LIST] });
      navigate(backUrl || AppRoutes.information);
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const onFinishUpload = (file: TExtendFile[]) => {
    const fileChosen = file[0] as File;
    setFileUploaded(fileChosen);
    setValue("file", file);
  };

  const removeFile = () => {
    setFileUploaded(null);
    methods.setValue("filePath", null);
  };

  const getStatus = () => {
    if (!id) {
      return INFORMATION_STATUS.DRAFT;
    }
    return getInformationStatus(getValues());
  };
  return (
    <Form methods={methods} onSubmit={onSubmit} shouldBlock={uploading} schema={informationSchema}>
      <TopPage
        title={previousData ? "お知らせ編集" : "新しいお知らせを作成する"}
        description={id ? `ステータス：${informationStatus}` : ""}
        enablePageBack={false}
      />

      <Stack width="100%" mt={2}>
        <SectionContainer>
          <Stack gap={2} className={"border"}>
            <Typography variant="sub16Bold">公開編集</Typography>
            <FormField control={control} name="type">
              <RadioGroup
                options={informationTypes.map((option, index) => {
                  if (index === 0) return { ...option, label: `${option.label}（トップページ上部）` };
                  return option;
                })}
                direction="column"
              />
            </FormField>
            <Stack gap={0.5}>
              <Typography variant="cap12Bold">公開開始日</Typography>
              <Typography variant="cap12">メンテナンス時間に合わせて表示する場合などに設定</Typography>
              <Stack direction="row" alignItems="center" gap={3}>
                <Typography variant="cap12" whiteSpace="nowrap">
                  設定
                </Typography>
                <FormField control={control} name="enableStartDate">
                  <Switch disabled={lockStartDate} />
                </FormField>
              </Stack>
              <FormField control={control} name="startDate" sx={{ maxWidth: 250 }} label="">
                <InputDatePicker format="YYYY/MM/DD" disabled={!enableStartDate || lockStartDate} />
              </FormField>
            </Stack>
            <Stack gap={0.5}>
              <Typography variant="cap12Bold">公開終了日</Typography>
              <Stack direction="row" alignItems="center" gap={3}>
                <Typography variant="cap12" whiteSpace="nowrap">
                  設定
                </Typography>
                <FormField control={control} name="enableEndDate">
                  <Switch disabled={lockEndDate} />
                </FormField>
              </Stack>
              <FormField control={control} name="endDate" sx={{ maxWidth: 250 }} label="">
                <InputDatePicker format="YYYY/MM/DD" disabled={!enableEndDate || lockEndDate} minDate={!previousData ? dayjs() : undefined} />
              </FormField>
            </Stack>
          </Stack>
        </SectionContainer>
        <SectionContainer>
          <Stack gap={2}>
            <Typography variant="sub16Bold">お知らせ内容</Typography>
            <FormField control={control} name="title">
              <Input size="lg" />
            </FormField>
            <FormField control={control} name="content">
              <Editor />
            </FormField>
          </Stack>
        </SectionContainer>
        <SectionContainer>
          <Stack gap={1}>
            <Typography variant="sub16Bold">ファイル添付</Typography>
            <Typography variant="cap12">1ファイルのみ添付可能です</Typography>
            <Button size="lg" variant="outline" disabled={uploading} onClick={() => setOpenUpload(true)}>
              ファイルを添付
            </Button>
            {fileUploaded && <FileItem file={fileUploaded} onRemove={removeFile} downloadable width={200} />}
            {filePath && !fileUploaded && <FileItem filePath={filePath} onRemove={removeFile} downloadable width={200} />}
          </Stack>
        </SectionContainer>
        <UploadFile
          isOpen={openUpload}
          onClose={() => setOpenUpload(false)}
          onCancel={() => setUploading(false)}
          uploadFor={FILE_UPLOAD_FOR.INFORMATION_FILE}
          rules={{ maxSize: convertKbToByte(10 * 1000), acceptMimeTypes: acceptMimeTypes }}
          options={{ multiple: false }}
          onFinishUpload={onFinishUpload}
          title={<Typography variant="body14Bold">ファイルをアップロードしてください。</Typography>}
        >
          <Stack alignItems="center" justifyContent="center" gap={2}>
            <Typography variant="body14" whiteSpace="pre-line" textAlign="center">{`ファイルを\nドラッグ&ドロップ`}</Typography>
            <Typography variant="body14">１ファイルのみ添付可能です</Typography>
          </Stack>
        </UploadFile>
        {openPreview && <PreviewInformationModal file={fileUploaded} open={openPreview} onClose={() => setOpenPreview(false)} />}
        <FooterContainer>
          <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
            <Button size="sm" variant="secondary" onClick={() => navigate(backUrl || AppRoutes.information)}>
              戻る
            </Button>
            <Stack direction="row" gap={3}>
              <Button variant="outline" size="sm" onClick={() => setOpenPreview(true)}>
                プレビュー
              </Button>
              <Button disabled={isSubmit} variant="outline" size="sm" onClick={handleSaveDraft}>
                下書き保存
              </Button>
              <Button disabled={isSubmit} size="sm" type="submit">
                更新
              </Button>
            </Stack>
          </Stack>
        </FooterContainer>
      </Stack>
    </Form>
  );
};

const SectionContainer = styled(Stack)`
  padding: 24px;
  border: 1px solid #e6e7e8;
  border-radius: 8px;
  background-color: #ffffff;
  margin-bottom: 32px;
`;
